import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Tab
} from '@mui/material';
import ConsoleTabs from '../common/ConsoleTabs';
import UserManagement from './UserManagement';
import UnifiedImportSettings from './UnifiedImportSettings';
import MappingAndUpdate from './MappingAndUpdate';
import AdoManagement from './AdoManagement';
import CommonLibrariesManagement from './CommonLibrariesManagement';
import ImportNotification from '../common/ImportNotification';
import TimelineConfigEditor from '../dashboard/TimelineConfigEditor';
import BugAnalysisReposRefreshPanel from './BugAnalysisReposRefreshPanel';
import SprintDataRefreshPanel from './SprintDataRefreshPanel';
import EngineeringProcessesAdmin from './EngineeringProcessesAdmin';
import ComplianceAdmin from './ComplianceAdmin';
import OpsCenterAdmin from './OpsCenterAdmin';
import ReliabilityAdmin from './ReliabilityAdmin';
import ArchiveAdmin from './ArchiveAdmin';
import TsiTeamsAdmin from './TsiTeamsAdmin';
import AccessTokensAdmin from './AccessTokensAdmin';
import WebhookLogViewer from './WebhookLogViewer';
import Register from '../auth/Register';
import { isSuperAdmin } from '../../services/authService';

/**
 * AdminPage component for admin settings and operations
 * @returns {JSX.Element} AdminPage component
 */
const AdminPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [refreshLogs, setRefreshLogs] = useState(0);
  const showSuperAdminTab = isSuperAdmin();
  const showAccessTokensTab = isSuperAdmin(); // Access Tokens only for SuperAdmin
  const accessTokensTabIndex = 13;
  const superAdminTabIndex = 13 + (showAccessTokensTab ? 1 : 0);
  const systemSettingsTabIndex = 13 + (showAccessTokensTab ? 1 : 0) + (showSuperAdminTab ? 1 : 0);
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    type: '',
    status: 'running',
    message: ''
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleImportComplete = () => {
    // Increment refresh counter to trigger useEffect in ImportLogs
    setRefreshLogs(prev => prev + 1);
  };

  const handleViewLogs = () => {
    setTabValue(5); // Switch to the Webhook Logs tab
  };

  const handleImportStart = (importType) => {
    setNotification({
      show: true,
      type: importType,
      status: 'running',
      message: 'Import is running in the background...'
    });
  };

  const handleImportFinish = (importType, result) => {
    setNotification({
      show: true,
      type: importType,
      status: result.success !== false ? 'success' : 'error',
      message: result.success !== false 
        ? `Successfully processed ${result.total || 0} items`
        : result.message || 'Import failed'
    });

    // Auto-hide success notifications after 5 seconds
    if (result.success !== false) {
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 5000);
    }

    // Refresh logs
    handleImportComplete();
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Panel
      </Typography>

        <Paper sx={{ mb: 2 }}>
          <ConsoleTabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Data Imports" />
            <Tab label="Mapping & Update" />
            <Tab label="User Management" />
            <Tab label="Register user" />
            <Tab label="ADO Management" />
            <Tab label="Webhook Logs" />
            <Tab label="Common Libraries" />
            <Tab label="Ops Center" />
            <Tab label="Reliability Program" />
            <Tab label="Engineering Processes" />
            <Tab label="Compliance" />
            <Tab label="Archive" />
            <Tab label="TSI Teams" />
            {showAccessTokensTab && <Tab label="Access Tokens" />}
            {showSuperAdminTab && <Tab label="Super Admin" />}
            <Tab label="System Settings" />
          </ConsoleTabs>
        </Paper>

        {tabValue === 0 && (
          <UnifiedImportSettings 
            onImportStart={handleImportStart}
            onImportComplete={handleImportFinish}
          />
        )}
        
        {tabValue === 1 && (
          <MappingAndUpdate />
        )}
        
        {tabValue === 2 && (
          <UserManagement />
        )}

        {tabValue === 3 && (
          <Box sx={{ py: 2 }}>
            <Register embedded />
          </Box>
        )}
        
        {tabValue === 4 && (
          <AdoManagement />
        )}

        {tabValue === 5 && (
          <WebhookLogViewer />
        )}

        {tabValue === 6 && (
          <CommonLibrariesManagement />
        )}

        {tabValue === 7 && (
          <OpsCenterAdmin />
        )}

        {tabValue === 8 && (
          <ReliabilityAdmin />
        )}

        {tabValue === 9 && (
          <EngineeringProcessesAdmin />
        )}

        {tabValue === 10 && (
          <ComplianceAdmin />
        )}

        {tabValue === 11 && (
          <ArchiveAdmin />
        )}

        {tabValue === 12 && (
          <Box sx={{ py: 2 }}>
            <TsiTeamsAdmin />
          </Box>
        )}

        {showAccessTokensTab && tabValue === accessTokensTabIndex && (
          <AccessTokensAdmin />
        )}

        {showSuperAdminTab && tabValue === superAdminTabIndex && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Super Admin
            </Typography>
            <Typography color="text.secondary" paragraph>
              This section is only visible to SuperAdmin users. SuperAdmin role is assigned or removed only via the database—it does not appear in User Management role options. Only SuperAdmins can see and manage other SuperAdmin users in User Management.
            </Typography>
          </Box>
        )}
        
        {tabValue === systemSettingsTabIndex && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              System Settings
            </Typography>
            <Box sx={{ mt: 4 }}>
              <SprintDataRefreshPanel />
              <BugAnalysisReposRefreshPanel />
              <TimelineConfigEditor />
            </Box>
          </Box>
        )}

      {/* Import Notification */}
      <ImportNotification
        show={notification.show}
        type={notification.type}
        status={notification.status}
        message={notification.message}
        onClose={handleNotificationClose}
      />
    </Box>
  );
};

export default AdminPage;
